/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
/** @jsx jsx */
import React, {useState, useMemo, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {FormattedMessage} from 'react-intl'
import Sticky from 'react-sticky-el'

//Import Slick and emotion
import {jsx, css} from '@emotion/react'
import Slider from 'react-slick'

// Chakra Components
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Heading,
    // Hooks
    Skeleton as ChakraSkeleton,
    ListItem,
    List,
    useMultiStyleConfig,
    HStack,
    Badge
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import DynamicImage from '@app/components/dynamic-image'
import {CartIcon} from '../custom-icons'

// Components
import ShopLookDrawer from '../product-view/ShopLookDrawer'
import {ResponsiveBynderVideo} from '../responsive-bynder-video'
import usePDCResponsiveElement from '../../hooks/use-pdc-responsive-element'
import {WrapperBdageLink} from '../wrapper-badge-link'
import useMultiSite from '../../hooks/use-multi-site'

// Constants
import {viewTypesOrderWithoutUNA, viewTypesOrderUNA} from '../../constants'

// Product Utils
import {createAutomatedLinkForTag} from '../../utils/product-utils'
import {productSetURLBuilder} from '../../utils/seo-utils'
import {isHydrated} from '../../utils/utils'

const EnterKeyNumber = 13

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})
    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Flex flexDirection="column">
                <AspectRatio ratio={1} {...styles.heroImageSkeleton}>
                    <ChakraSkeleton />
                </AspectRatio>
                <Flex>
                    {new Array(4).fill(0).map((_, index) => (
                        <AspectRatio ratio={1} {...styles.thumbnailImageSkeleton} key={index}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    ))}
                </Flex>
            </Flex>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool
}

/**
 * LinkedProductSets Component
 *
 * This functional component renders a section containing a heading and a button,
 * which are derived from a linked product set object. It is designed to be used
 * within a larger UI framework where linked product sets need to be displayed.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.linkedProductSets - An object containing data for the linked product set.
 * @param {string} props.linkedProductSets.title - The title to be displayed in the heading.
 * @param {string} props.linkedProductSets.linkURL - The URL to be used for the button link.
 * @param {string} props.linkedProductSets.linkLabel - The label to be displayed on the button.
 * @param {Object} props.styles - An object containing style properties for the component.
 * @param {Object} props.styles.linkedProductSets - Style properties to be applied to the Box component.
 *
 * @returns {JSX.Element} A JSX element representing the linked product set section.
 */
const LinkedProductSets = ({linkedProductSets, styles}) => {
    const {title, productID, linkLabel} = linkedProductSets

    if (!productID) return null

    const formattedLink = productSetURLBuilder(productID)

    return (
        <Box {...styles.linkedProductSets}>
            <Heading as={'div'} {...styles.linkedProductSetsTitle}>
                {title}
            </Heading>
            <Button variant={'tertiary'} to={formattedLink} as={Link}>
                {linkLabel}
            </Button>
        </Box>
    )
}

LinkedProductSets.propTypes = {
    linkedProductSets: PropTypes.object,
    styles: PropTypes.object
}

/**
 * HeroImage is a React functional component that displays a responsive image with a skeleton placeholder to prevent Cumulative Layout Shift (CLS) during loading.
 *
 * The component checks if the app is hydrated and if an image object is provided. While the image is loading, a ChakraSkeleton placeholder is rendered to maintain layout stability. Once the image is loaded, it is displayed using the DynamicImage component with responsive widths and eager loading for optimal performance.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.image - The image object containing source links and alt text.
 * @param {Object} props.styles - An object containing style definitions, particularly for desktop display.
 * @param {...Object} props - Additional props passed to the DynamicImage component.
 *
 * @returns {JSX.Element} A responsive image with a skeleton placeholder during loading, or a skeleton if no image is provided or the app is not hydrated.
 */
const HeroImage = ({image, styles, ...props}) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false)

    return isHydrated() && image ? (
        <>
            {
                // Prevent CLS while image is loading
                !isImageLoaded && <ChakraSkeleton aspectRatio="4 / 5" width="100%" />
            }

            <DynamicImage
                {...styles.imageDesktop}
                src={`${image?.disBaseLink || image?.link}[?sw={width}]`}
                widths={['100vw', '100vw', '100vw', '464px', '464px', '464px']}
                imageProps={{
                    width: '100%',
                    loading: 'eager',
                    fetchpriority: 'high',
                    alt: `${image?.alt}`,
                    title: '',
                    onLoad: () => setIsImageLoaded(true)
                }}
                {...props}
            />
        </>
    ) : (
        <ChakraSkeleton aspectRatio="4 / 5" width="100%" />
    )
}

HeroImage.propTypes = {
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    styles: PropTypes.object
}

/**
 * ImageGallery is a React component that displays a gallery of product images and videos, supporting both desktop and mobile layouts, modal and non-modal views, and advanced product merchandising features.
 *
 * The component dynamically selects and renders image groups based on the current product variation attributes and view types, supporting features such as:
 * - Responsive display with carousel on mobile and grid on desktop.
 * - Support for both images and videos, with responsive video handling.
 * - Lazy loading of images for performance optimization.
 * - Display of product tags and "Perfect Your Look" upsell drawer.
 * - Integration with linked product sets for enhanced merchandising.
 * - Modal view with selectable thumbnails for image navigation.
 *
 * Props:
 * @param {Array} imageGroups - Array of image group objects, each containing images and metadata.
 * @param {Object} selectedVariationAttributes - Selected product variation attributes (e.g., color, size).
 * @param {string} size - Size variant for styling the gallery.
 * @param {boolean} lazy - If true, images are lazy-loaded; otherwise, loaded eagerly.
 * @param {boolean} isModal - If true, renders the gallery in modal mode with thumbnails.
 * @param {boolean} isCarouselOnMobile - If true, enables carousel view on mobile devices.
 * @param {Object} product - Product object containing details and merchandising metadata.
 * @param {boolean} isProductSets - If true, disables tag and shop-the-look features.
 * @param {Array} perfectYourLookProducts - Array of products for the "Perfect Your Look" upsell feature.
 *
 * The component leverages hooks and utilities for state management, responsive design, and URL parameter handling. It supports advanced merchandising scenarios, such as displaying badges, shop-the-look drawers, and linked product sets, and is highly customizable via props and style configurations.
 */
const ImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false,
    isModal = false,
    isCarouselOnMobile = true,
    product,
    isProductSets = false,
    perfectYourLookProducts = []
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const styles = useMultiStyleConfig('ImageGallery', {size})
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    const selectedColor = urlParams.get('color') || product.c_color
    const {buildUrl} = useMultiSite()
    const automatedTagLink = buildUrl(createAutomatedLinkForTag(product))

    useEffect(() => {
        // reset the selected index when location search changes
        setSelectedIndex(0)
    }, [location.search])

    /**
     * Memoized function to determine the available image group based on view types and selected variation attributes.
     *
     * This function iterates over a predefined order of view types and attempts to find an image group
     * that matches the current selection criteria. It uses the `findImageGroupBy` utility to search
     * through the provided image groups. If a matching group with images is found, it is returned.
     * Otherwise, the function returns null.
     *
     * Dependencies:
     * - `imageGroups`: An array of image groups to search through.
     * - `selectedVariationAttributes`: The current set of selected variation attributes used to filter image groups.
     *
     * Returns:
     * - An image group object that matches the criteria and contains images, or null if no such group is found.
     */
    const availableViewTypesGroup = useMemo(() => {
        const groups = []
        // Take the correct order for viewTypes
        const viewTypesOrder =
            product?.c_orliweb_stat03_code_marche === 'UNA'
                ? viewTypesOrderUNA
                : viewTypesOrderWithoutUNA

        for (const viewType of viewTypesOrder) {
            const group = findImageGroupBy(imageGroups, {
                viewType,
                selectedVariationAttributes
            })

            if (group && group.images.length > 0) {
                // Add a viewType to each Image
                group.images = group.images.map((image) => ({
                    ...image,
                    viewType
                }))
                groups.push(group)
            }
        }

        return groups
    }, [imageGroups, selectedVariationAttributes])

    const allImages = availableViewTypesGroup.flatMap((group) => group.images)
    const isVideo = availableViewTypesGroup.find((group) => group.viewType === 'video')
    const videoInfo = isVideo ? isVideo.images[0] : null
    const availableViewTypesGroupWithAllImages = {
        images: allImages
    }

    const heroImage = availableViewTypesGroupWithAllImages?.images?.[selectedIndex]
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    //One single mediaID for all the breakpoints
    const videosPerBreakpoint = usePDCResponsiveElement({
        smallEl: isVideo ? videoInfo?.title : null, // Assuming 'title' is used as mediaId
        mediumEl: isVideo ? videoInfo?.title : null,
        largeEl: isVideo ? videoInfo?.title : null,
        xlargeEl: isVideo ? videoInfo?.title : null,
        xxlargeEl: isVideo ? videoInfo?.title : null
    })

    const sliderSettings = {
        dots: true, // Activate dots
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false
    }

    const slickStyles = css`
        /* override touch-action: pan-y from slick.css to restore the browser native gesture handling */
        /* (including two-finger pinch-to-zoom) */
        .slick-slider {
            -ms-touch-action: auto !important;
            touch-action: auto !important;
        }

        .slick-dots {
            position: absolute;
            bottom: 10px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            justify-content: center;
            width: 100%;
            max-width: 100vw;
        }

        .slick-dots li {
            margin: 0 5px;
        }

        .slick-dots li button:before {
            font-size: 30px;
            color: #fff;
            content: '—';
            opacity: initial;
        }

        .slick-dots li.slick-active button:before {
            color: black;
        }
    `

    // Check position of the video before show tag and shopthelook drawer
    const isFirstMediaVideo =
        availableViewTypesGroupWithAllImages?.images?.[0]?.viewType === 'video'

    // Check if tag and shopthelook Drawer should be put at second position or not
    const tagAndShopLookIndex = isFirstMediaVideo ? 1 : 0

    const linkedProductSetsData = product.c_linkedProductSets
        ? JSON.parse(product.c_linkedProductSets)
        : null

    return (
        <Flex direction="column" css={slickStyles}>
            {heroImage ? (
                <Box {...styles.heroImageGroupDesktop}>
                    {/* Product Images on Desktop */}
                    <Box display={isCarouselOnMobile ? {base: 'none', lg: 'block'} : 'block'}>
                        {
                            // PDP display
                            !isModal
                                ? availableViewTypesGroupWithAllImages?.images?.map(
                                      (media, index) => {
                                          const displayTagAndShopLook =
                                              index === tagAndShopLookIndex
                                          const linkedProductSets =
                                              linkedProductSetsData?.[selectedColor]?.[
                                                  media?.viewType
                                              ]

                                          return (
                                              <Box
                                                  position="relative"
                                                  key={index}
                                                  style={{
                                                      order:
                                                          media?.disBaseLink || media?.link
                                                              ? index + 1 ===
                                                                parseInt(media?.position)
                                                                  ? index + 1
                                                                  : index + 2
                                                              : media?.position
                                                  }}
                                                  {...styles.aspectRatioHeroImage}
                                              >
                                                  {media?.viewType === 'video' ? (
                                                      <ResponsiveBynderVideo
                                                          mediaIds={videosPerBreakpoint}
                                                          autoplay={false}
                                                      />
                                                  ) : (
                                                      <DynamicImage
                                                          {...styles.imageDesktop}
                                                          src={`${
                                                              media?.disBaseLink || media?.link
                                                          }[?sw={width}]`}
                                                          widths={[
                                                              '100vw',
                                                              '100vw',
                                                              '100vw',
                                                              '816px',
                                                              '816px',
                                                              '50vw'
                                                          ]}
                                                          imageProps={{
                                                              width: '100%',
                                                              loading:
                                                                  index === 0 ? 'eager' : 'lazy',
                                                              fetchpriority:
                                                                  index === 0 ? 'high' : undefined,
                                                              alt: `${media?.alt}`,
                                                              title: ''
                                                          }}
                                                      />
                                                  )}
                                                  {displayTagAndShopLook && !isProductSets && (
                                                      <>
                                                          {product?.c_tag && (
                                                              <HStack {...styles.badgeGroupPDP}>
                                                                  <WrapperBdageLink
                                                                      link={automatedTagLink}
                                                                  >
                                                                      <Badge
                                                                          {...styles.badgeCustomStyle}
                                                                          data-testid="product-badge"
                                                                          color={product.c_tagColor}
                                                                      >
                                                                          {product.c_tag}
                                                                      </Badge>
                                                                  </WrapperBdageLink>
                                                              </HStack>
                                                          )}
                                                          {perfectYourLookProducts.length > 0 && (
                                                              <Sticky
                                                                  mode="bottom"
                                                                  bottomOffset={10}
                                                              >
                                                                  <ShopLookDrawer
                                                                      key={product?.id}
                                                                      products={
                                                                          perfectYourLookProducts
                                                                      }
                                                                      buttonProps={{
                                                                          styles: 'shopLookDrawerButton',
                                                                          leftIcon: (
                                                                              <CartIcon boxSize="3" />
                                                                          ),
                                                                          text: (
                                                                              <FormattedMessage
                                                                                  defaultMessage="Perfect your look"
                                                                                  id="product_view.link.perfect_your_look"
                                                                              />
                                                                          )
                                                                      }}
                                                                  />
                                                              </Sticky>
                                                          )}
                                                      </>
                                                  )}
                                                  {linkedProductSets && (
                                                      <LinkedProductSets
                                                          linkedProductSets={linkedProductSets}
                                                          styles={styles}
                                                      />
                                                  )}
                                              </Box>
                                          )
                                      }
                                  )
                                : // Modal display with hero image selectable through thumbnails
                                  heroImage?.viewType !== 'video' && (
                                      <Box {...styles.aspectRatioHeroImage} position={'relative'}>
                                          <HeroImage image={heroImage} styles={styles} />
                                      </Box>
                                  )
                        }
                    </Box>
                    {/* End Product Images on Desktop */}

                    {/* Carousel Product Images on Mobile */}
                    {heroImage && isCarouselOnMobile && (
                        <HideOnDesktop>
                            <Slider
                                {...sliderSettings}
                                dots={availableViewTypesGroupWithAllImages?.images?.length > 1} // show dots only if there is more than one media
                                infinite={availableViewTypesGroupWithAllImages?.images?.length > 1}
                                speed={500}
                                slidesToShow={1}
                                slidesToScroll={1}
                            >
                                {availableViewTypesGroupWithAllImages?.images?.map(
                                    (media, index) => {
                                        const displayTagAndShopLook = index === tagAndShopLookIndex
                                        let linkedProductSets = null
                                        if (product?.c_linkedProductSets) {
                                            const linkedProductSetsData = JSON.parse(
                                                product.c_linkedProductSets
                                            )
                                            linkedProductSets =
                                                linkedProductSetsData?.[selectedColor]?.[
                                                    media?.viewType
                                                ]
                                        }
                                        return (
                                            <Box key={index} position="relative">
                                                <AspectRatio ratio={4 / 5}>
                                                    {media?.viewType === 'video' ? (
                                                        <Box zIndex={1}>
                                                            <ResponsiveBynderVideo
                                                                mediaIds={videosPerBreakpoint}
                                                                autoplay={false}
                                                            />
                                                        </Box>
                                                    ) : (
                                                        <DynamicImage
                                                            src={`${
                                                                media?.disBaseLink || media?.link
                                                            }[?sw={width}]`}
                                                            widths={[
                                                                '100vw',
                                                                '100vw',
                                                                '100vw',
                                                                '816px',
                                                                '816px',
                                                                '50vw'
                                                            ]}
                                                            imageProps={{
                                                                width: '100%',
                                                                loading:
                                                                    index === 0 ? 'eager' : 'lazy',
                                                                fetchpriority:
                                                                    index === 0
                                                                        ? 'high'
                                                                        : undefined,
                                                                aspectRatio: 4 / 5,
                                                                alt: `${media?.alt}`,
                                                                title: ''
                                                            }}
                                                        />
                                                    )}
                                                </AspectRatio>
                                                {displayTagAndShopLook && !isProductSets && (
                                                    <>
                                                        {product?.c_tag && (
                                                            <HStack {...styles.badgeGroupPDP}>
                                                                <WrapperBdageLink
                                                                    link={automatedTagLink}
                                                                >
                                                                    <Badge
                                                                        {...styles.badgeCustomStyle}
                                                                        data-testid="product-badge"
                                                                        color={product.c_tagColor}
                                                                    >
                                                                        {product.c_tag}
                                                                    </Badge>
                                                                </WrapperBdageLink>
                                                            </HStack>
                                                        )}
                                                        {perfectYourLookProducts.length > 0 && (
                                                            <Sticky mode="bottom" bottomOffset={10}>
                                                                <ShopLookDrawer
                                                                    products={
                                                                        perfectYourLookProducts
                                                                    }
                                                                    buttonProps={{
                                                                        styles: 'shopLookDrawerButton',
                                                                        leftIcon: (
                                                                            <CartIcon boxSize="3" />
                                                                        ),
                                                                        text: (
                                                                            <FormattedMessage
                                                                                defaultMessage="Perfect your look"
                                                                                id="product_view.link.perfect_your_look"
                                                                            />
                                                                        )
                                                                    }}
                                                                />
                                                            </Sticky>
                                                        )}
                                                    </>
                                                )}
                                                {linkedProductSets && (
                                                    <LinkedProductSets
                                                        linkedProductSets={linkedProductSets}
                                                        styles={styles}
                                                    />
                                                )}
                                            </Box>
                                        )
                                    }
                                )}
                            </Slider>
                        </HideOnDesktop>
                    )}
                    {/* End Product Images on Mobile */}
                </Box>
            ) : null}

            {/* Thumbnails Product Images */}
            {isModal && (
                <List {...styles.thumbnailImageGroup}>
                    {availableViewTypesGroupWithAllImages?.images?.map((image, index) => {
                        const selected = index === selectedIndex
                        return (
                            image?.viewType !== 'video' && (
                                <ListItem
                                    {...styles.thumbnailImageItem}
                                    key={`thumbnail-${image?.viewType}-${
                                        image?.disBaseLink || image?.link
                                    }`}
                                >
                                    <Box
                                        as="button"
                                        aria-pressed={selected ? 'true' : 'false'}
                                        onKeyDown={(e) => {
                                            if (e.keyCode === EnterKeyNumber) {
                                                return setSelectedIndex(index)
                                            }
                                        }}
                                        onClick={() => setSelectedIndex(index)}
                                        data-testid="image-gallery-thumbnails"
                                        position={'relative'}
                                        _after={{
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            cursor: 'pointer',
                                            zIndex: 1,
                                            borderColor: 'fullBlack',
                                            borderWidth: selected ? [1.5, null, 2] : 0,
                                            borderStyle: 'solid'
                                        }}
                                    >
                                        <DynamicImage
                                            src={`${
                                                image?.disBaseLink || image?.link
                                            }[?sw={width}]`}
                                            widths={Object.values(styles.thumbnailImageItem.width)}
                                            height="100%"
                                            imageProps={{
                                                width: '100%',
                                                height: '100%',
                                                loading: loadingStrategy,
                                                objectFit: 'cover',
                                                alt: image?.alt
                                            }}
                                        />
                                    </Box>
                                </ListItem>
                            )
                        )
                    })}
                </List>
            )}
        </Flex>
    )
}

ImageGallery.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Image gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool,
    product: PropTypes.object,
    isModal: PropTypes.bool,
    isCarouselOnMobile: PropTypes.bool,
    isProductSets: PropTypes.bool,
    perfectYourLookProducts: PropTypes.array
}

export default ImageGallery
